import Popper from '@mui/material/Popper';
import React from 'react';
import { setOptions, updateValue } from '../../Lib/redux/slices/components';
import {
  getComponentByNumericalId,
  isGoogleAddressComponent,
} from '../__helpers__/component.helpers';

export const loadScript = (src, position, id) => {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
};

/**
 * isThisLastGoogleAddressComponentOnPage
 * @returns boolean
 * @description Checks if the current Google address component is the last one on the page - this is required as we only want to attempt an auto advance is there is more than one Google address component on the page and we are selecting an option
 */
export const isThisLastGoogleAddressComponentOnPage = ({
  components,
  componentId,
}) => {
  // Get the component with the given componentId
  const targetComponent = getComponentByNumericalId({
    components,
    componentId,
  });

  if (!targetComponent) {
    throw new Error(`Component with id ${componentId} not found`);
  }

  const targetPageId = targetComponent.component.page;

  const googleAddressComponentsOnTargetPage = Object.values(components).filter(
    (component) => {
      return (
        isGoogleAddressComponent(component) &&
        component.component.page === targetPageId
      );
    }
  );

  // If there are no Google address components, return false
  if (googleAddressComponentsOnTargetPage.length === 0) {
    return false;
  }

  // If there is only one Google address component, it is the last by default
  if (googleAddressComponentsOnTargetPage.length === 1) {
    return googleAddressComponentsOnTargetPage[0].component.id === componentId;
  }

  // Find the index of the target component among the Google address components
  const targetIndex = googleAddressComponentsOnTargetPage.findIndex(
    (componentWrapper) => componentWrapper.component.id === componentId
  );

  // Check if the target component is the last one in the list
  return targetIndex === googleAddressComponentsOnTargetPage.length - 1;
};

export const getAddressValue = ({ addressType, addressComponent }) => {
  const googleAddressTypeMap = {
    premise: 'long_name',
    route: 'long_name',
    locality: 'long_name',
    postal_code: 'long_name',
    postal_town: 'short_name',
    country: 'short_name',
  };

  let premise = '';
  let route = '';
  let locality = '';
  let postalCode = '';
  let postalTown = '';

  const addressComponentType =
    addressComponent[googleAddressTypeMap[addressType]];

  switch (addressType) {
    case 'premise':
      premise = addressComponentType;
      return premise;

    case 'route':
      route = addressComponentType;
      return route;

    case 'locality':
      locality = addressComponentType;
      return locality;

    case 'postal_town':
      postalTown = addressComponentType;
      return postalTown;

    case 'postal_code':
      postalCode = addressComponentType;
      return postalCode;

    default:
      return addressComponentType;
  }
};

const getComponentId = (mappedQuestionId, questionIdToComponentIdMap) => {
  return questionIdToComponentIdMap[mappedQuestionId];
};

export const setGoogleAddressMappedInputs = ({
  addressComponents,
  addressMap,
  questionIdToComponentIdMap,
  dispatch,
}) => {
  if (!addressComponents) {
    return;
  }

  addressComponents.map((addressComponent) => {
    const addressType = addressComponent.types[0];
    const componentId = getComponentId(
      addressMap[addressType],
      questionIdToComponentIdMap
    );
    const value = getAddressValue({ addressType, addressComponent });
    if (componentId === undefined) {
      return;
    }

    return dispatch(updateValue({ componentId, value }));
  });
};

export const createAutoCompletePopper = (autoCompleteRef) => {
  const AutoCompletePopper = (props) => {
    const inputWrapper =
      autoCompleteRef.current.getElementsByClassName('MuiInputBase-root')[0];
    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <Popper
        {...props}
        style={{
          width: inputWrapper ? inputWrapper.clientWidth : null,
        }}
        anchorEl={inputWrapper}
      />
    );
  };

  return AutoCompletePopper;
};

// Makes the request to get place predictions
export const requestPlacePredictions = (
  request,
  service,
  callback,
  activeRequestId,
  currentRequestId
) => {
  // developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service?_gl=1*1wmibv7*_up*MQ..*_ga*NDczMDE0ODg2LjE3MjQyNDg2ODA.*_ga_NRWSTWS78N*MTcyNDI0ODY3OS4xLjAuMTcyNDI0ODc3MC4wLjAuMA..#ComponentRestrictions

  if (service?.current) {
    service.current.getPlacePredictions(request, (results, status) => {
      if (activeRequestId === currentRequestId) {
        callback(results, status);
      }
    });
  }
};

export const handleSettingPlacePredictions = ({
  status,
  results,
  component_id,
  reduxDispatch,
}) => {
  if (status === window?.google?.maps?.places?.PlacesServiceStatus?.OK) {
    reduxDispatch(setOptions({ componentId: component_id, options: results }));
  } else if (
    status === window?.google?.maps?.places?.PlacesServiceStatus?.ZERO_RESULTS
  ) {
    reduxDispatch(setOptions({ componentId: component_id, options: [] }));
  }
};

export const getGeocodeAddresses = async ({
  newValue,
  autocompleteService,
}) => {
  if (!newValue || !newValue.place_id) return null;

  try {
    const { results } = await autocompleteService.geocoder.geocode({
      placeId: newValue.place_id,
    });

    if (results.length && results[0]?.address_components.length) {
      return results[0].address_components;
    }
    return null;
  } catch (error) {
    return null;
  }
};
